import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_CARD,
  AE_ICON_SIZE,
  AE_EDITABLE_INPUT,
  AE_LABEL,
  AE_MUTED,
  AE_SELECT,
  AE_TEXTAREA,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeTemplateDetail } from '@/types/assessmentEngine';
import { BuilderIconButton } from './BuilderIconButton';
import { syncDemographicSection } from './demographicSync';
import {
  applyDimensionNamesToSections,
  mergedConfigFromTemplate,
  refreshAndPersistDimensions,
} from './dimensionSync';
import { internalSectionCode } from './numberingUtils';
import {
  scoringConfigForParticipation,
  sectionParticipatesInScoring,
} from './sectionScoring';
import {
  AE_DEMOGRAPHIC_FIELD_KEYS,
  DEFAULT_SCALE_PRESETS,
  DEMOGRAPHICS_SECTION_CODE,
  type AeTemplateDefaultConfig,
  type AeTemplateDimension,
  type AeTemplateScalePreset,
} from './templateConfigTypes';
import { useDebouncedSave } from './useDebouncedSave';
import { useTemplateSaveError } from './useTemplateSaveError';
import { ScalePresetsEditor } from './ScalePresetsEditor';
import { MaturityMapEditor } from './MaturityMapEditor';
import { parseMaturityMap } from './maturityMapUtils';
import { TemplateMetadataEditor } from './TemplateMetadataEditor';
import { beginStructureDelete, endStructureDeleteSoon } from './structureDeleteGuard';
import { propagateScalePresetsToQuestions } from './scalePresetLink';

interface TemplateSettingsPanelProps {
  template: AeTemplateDetail;
  readOnly?: boolean;
  onReload: () => void;
}

/** Template-level dimensions (synced with sections), scale presets, and demographic defaults. */
export const TemplateSettingsPanel: React.FC<TemplateSettingsPanelProps> = ({
  template,
  readOnly,
  onReload,
}) => {
  const { t } = useLanguage();
  const onSaveError = useTemplateSaveError();
  const [config, setConfig] = useState<AeTemplateDefaultConfig>(() => mergedConfigFromTemplate(template));
  const [saving, setSaving] = useState(false);
  const [syncingDemo, setSyncingDemo] = useState(false);
  const [contextNames, setContextNames] = useState<Record<string, string>>({});

  const topLevelSections = useMemo(
    () =>
      template.sections
        .filter((s) => !s.parent_section_id && s.code !== DEMOGRAPHICS_SECTION_CODE)
        .sort((a, b) => a.sort_order - b.sort_order),
    [template.sections]
  );

  const serverConfigRevision = useMemo(
    () => JSON.stringify(template.default_config ?? {}),
    [template.id, template.default_config]
  );

  useEffect(() => {
    setConfig(mergedConfigFromTemplate(template));
    const names: Record<string, string> = {};
    for (const section of template.sections) {
      if (
        !section.parent_section_id &&
        section.code !== DEMOGRAPHICS_SECTION_CODE &&
        !sectionParticipatesInScoring(section.scoring_config)
      ) {
        names[section.id] = section.name;
      }
    }
    setContextNames(names);
  }, [serverConfigRevision, template]);

  const persistDimensions = useCallback(async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const dimensions = config.dimensions ?? [];
      await applyDimensionNamesToSections(template.id, dimensions);
      await assessmentEngineService.updateTemplate(template.id, {
        default_config: config as Record<string, unknown>,
      });
    } finally {
      setSaving(false);
    }
  }, [readOnly, template.id, config]);

  useDebouncedSave(persistDimensions, [config.dimensions, readOnly], 1000, { onError: onSaveError });

  const persistContextNames = useCallback(async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      for (const section of topLevelSections) {
        if (sectionParticipatesInScoring(section.scoring_config)) continue;
        const name = contextNames[section.id]?.trim();
        if (name && name !== section.name) {
          await assessmentEngineService.updateSection(template.id, section.id, { name });
        }
      }
    } finally {
      setSaving(false);
    }
  }, [readOnly, template.id, topLevelSections, contextNames]);

  useDebouncedSave(persistContextNames, [contextNames, readOnly], 1000, { onError: onSaveError });

  const handleScoringToggle = async (sectionId: string, participates: boolean) => {
    if (readOnly) return;
    setSaving(true);
    try {
      await assessmentEngineService.updateSection(template.id, sectionId, {
        scoring_config: scoringConfigForParticipation(participates),
      });
      await refreshAndPersistDimensions(template.id);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  const persistOtherConfig = useCallback(async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const previousPresets =
        (mergedConfigFromTemplate(template).scale_presets as AeTemplateScalePreset[] | undefined) ??
        DEFAULT_SCALE_PRESETS;
      const nextPresets = config.scale_presets ?? DEFAULT_SCALE_PRESETS;
      await assessmentEngineService.updateTemplate(template.id, {
        default_config: config as Record<string, unknown>,
      });
      // Re-copy shared scale fields onto linked questions so take/scoring stay in sync
      // and Survey design keeps showing the named preset after label/range edits.
      await propagateScalePresetsToQuestions(template.id, template, previousPresets, nextPresets);
      // Refresh the shared template snapshot so Survey design (kept mounted in
      // the sibling tab) immediately receives new/updated shared scale presets.
      onReload();
    } finally {
      setSaving(false);
    }
  }, [readOnly, template, config, onReload]);

  const otherConfigRevision = useMemo(
    () =>
      JSON.stringify({
        demographics: config.demographics,
        scale_presets: config.scale_presets,
        respondent_framing: config.respondent_framing,
        maturity_map: config.maturity_map,
      }),
    [config.demographics, config.scale_presets, config.respondent_framing, config.maturity_map]
  );

  useDebouncedSave(persistOtherConfig, [otherConfigRevision, readOnly], 1000, { onError: onSaveError });

  const applyDemographics = async () => {
    if (readOnly || !config.demographics) return;
    setSyncingDemo(true);
    try {
      await syncDemographicSection(template.id, template, config.demographics, t);
      onReload();
    } finally {
      setSyncingDemo(false);
    }
  };

  const updateDimensionAt = (dimIdx: number, patch: Partial<AeTemplateDimension>) => {
    setConfig((c) => {
      const next = [...(c.dimensions ?? [])];
      next[dimIdx] = { ...next[dimIdx], ...patch };
      return { ...c, dimensions: next };
    });
  };

  const updateSubDimensionAt = (dimIdx: number, subIdx: number, name: string) => {
    setConfig((c) => {
      const dims = [...(c.dimensions ?? [])];
      const dim = dims[dimIdx];
      const subs = [...(dim.sub_dimensions ?? [])];
      subs[subIdx] = { ...subs[subIdx], name };
      dims[dimIdx] = { ...dim, sub_dimensions: subs };
      return { ...c, dimensions: dims };
    });
  };

  const addSection = async () => {
    const label = t('assessmentEngine.builder.newDimensionDefault');
    const idx = topLevelSections.length;
    await assessmentEngineService.createSection(template.id, {
      code: internalSectionCode(idx),
      name: label,
      sort_order: idx,
      scoring_config: scoringConfigForParticipation(true),
    });
    await refreshAndPersistDimensions(template.id);
    onReload();
  };

  const addSubDimension = async (sectionId: string) => {
    const parent = template.sections.find((s) => s.id === sectionId);
    if (!parent) return;
    const idx = parent.subsections.length;
    const parentNum = topLevelSections.findIndex((s) => s.id === sectionId);
    await assessmentEngineService.createSection(template.id, {
      parent_section_id: sectionId,
      code: internalSectionCode(idx, parentNum >= 0 ? parentNum : 0),
      name: t('assessmentEngine.builder.newSubDimensionDefault'),
      sort_order: idx,
    });
    await refreshAndPersistDimensions(template.id);
    onReload();
  };

  const removeDimension = async (sectionId: string) => {
    const section = topLevelSections.find((s) => s.id === sectionId);
    if (
      !window.confirm(
        t('assessmentEngine.builder.confirmDeleteSectionNamed', {
          name: section?.name ?? sectionId,
        })
      )
    ) {
      return;
    }
    beginStructureDelete(sectionId);
    try {
      await assessmentEngineService.deleteSection(template.id, sectionId);
      await refreshAndPersistDimensions(template.id);
      onReload();
    } finally {
      endStructureDeleteSoon(sectionId);
    }
  };

  const removeSubDimension = async (subSectionId: string) => {
    const sub = topLevelSections
      .flatMap((s) => s.subsections)
      .find((s) => s.id === subSectionId);
    if (
      !window.confirm(
        t('assessmentEngine.builder.confirmDeleteSectionNamed', {
          name: sub?.name ?? subSectionId,
        })
      )
    ) {
      return;
    }
    beginStructureDelete(subSectionId);
    try {
      await assessmentEngineService.deleteSection(template.id, subSectionId);
      await refreshAndPersistDimensions(template.id);
      onReload();
    } finally {
      endStructureDeleteSoon(subSectionId);
    }
  };

  return (
    <div className={`${AE_CARD} space-y-8`}>
      <TemplateMetadataEditor template={template} onReload={onReload} />

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
            {t('assessmentEngine.builder.respondentFraming')}
          </h3>
          <p className={AE_MUTED}>{t('assessmentEngine.builder.respondentFramingHint')}</p>
        </div>

        <div className="space-y-3 border border-[var(--color-border)] rounded-md p-4">
          <h4 className="text-sm font-medium text-[var(--color-surface-text)]">
            {t('assessmentEngine.builder.openingSection')}
          </h4>
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.builder.openingPurpose')}</label>
            <p className={`${AE_MUTED} text-xs mb-1`}>{t('assessmentEngine.builder.openingPurposeHint')}</p>
            <textarea
              className={`${AE_TEXTAREA} w-full min-h-[72px]`}
              disabled={readOnly}
              value={config.respondent_framing?.opening?.purpose ?? ''}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  respondent_framing: {
                    ...c.respondent_framing,
                    opening: {
                      ...c.respondent_framing?.opening,
                      purpose: e.target.value,
                    },
                  },
                }))
              }
            />
          </div>
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.builder.openingInstructions')}</label>
            <p className={`${AE_MUTED} text-xs mb-1`}>{t('assessmentEngine.builder.openingInstructionsHint')}</p>
            <textarea
              className={`${AE_TEXTAREA} w-full min-h-[96px]`}
              disabled={readOnly}
              value={config.respondent_framing?.opening?.instructions ?? ''}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  respondent_framing: {
                    ...c.respondent_framing,
                    opening: {
                      ...c.respondent_framing?.opening,
                      instructions: e.target.value,
                    },
                  },
                }))
              }
            />
          </div>
        </div>

        <div className="space-y-3 border border-[var(--color-border)] rounded-md p-4">
          <h4 className="text-sm font-medium text-[var(--color-surface-text)]">
            {t('assessmentEngine.builder.closingSection')}
          </h4>
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.builder.closingMessage')}</label>
            <p className={`${AE_MUTED} text-xs mb-1`}>{t('assessmentEngine.builder.closingMessageHint')}</p>
            <textarea
              className={`${AE_TEXTAREA} w-full min-h-[72px]`}
              disabled={readOnly}
              value={config.respondent_framing?.closing?.message ?? ''}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  respondent_framing: {
                    ...c.respondent_framing,
                    closing: {
                      ...c.respondent_framing?.closing,
                      message: e.target.value,
                    },
                  },
                }))
              }
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
            {t('assessmentEngine.builder.dimensions')}
          </h3>
          {saving && <span className={AE_MUTED}>{t('assessmentEngine.builder.saving')}</span>}
        </div>
        <p className={AE_MUTED}>{t('assessmentEngine.builder.dimensionsHint')}</p>
        {topLevelSections.length === 0 && (
          <p className={AE_MUTED}>{t('assessmentEngine.builder.noDimensionsYet')}</p>
        )}
        {topLevelSections.length > 0 && (
        <ul className="ml-3 border-l border-[var(--color-border)] space-y-4 list-none m-0 p-0">
          {topLevelSections.map((section) => {
            const participates = sectionParticipatesInScoring(section.scoring_config);
            const dimIdx = (config.dimensions ?? []).findIndex((d) => d.section_id === section.id);
            const dim = dimIdx >= 0 ? config.dimensions![dimIdx] : null;

            return (
            <li key={section.id} className="relative pl-4">
              <span
                className="pointer-events-none absolute left-0 top-[1.125rem] w-4 border-t border-[var(--color-border)]"
                aria-hidden
              />
              <div className="flex flex-wrap gap-x-3 gap-y-2 items-center">
                <input
                  className={`${AE_EDITABLE_INPUT} text-sm flex-1 min-w-[12rem]`}
                  value={participates && dim ? dim.name : (contextNames[section.id] ?? section.name)}
                  disabled={readOnly}
                  placeholder={t('assessmentEngine.builder.dimensionName')}
                  onChange={(e) => {
                    if (participates && dimIdx >= 0) {
                      updateDimensionAt(dimIdx, { name: e.target.value });
                    } else {
                      setContextNames((prev) => ({ ...prev, [section.id]: e.target.value }));
                    }
                  }}
                />
                {!readOnly && (
                  <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)] shrink-0">
                    <input
                      type="checkbox"
                      checked={participates}
                      onChange={(e) => void handleScoringToggle(section.id, e.target.checked)}
                      className="accent-[var(--color-accent-bg)]"
                    />
                    {t('assessmentEngine.builder.includeInScoring')}
                  </label>
                )}
                {!readOnly && participates && (
                  <BuilderIconButton
                    label={t('assessmentEngine.builder.addSubDimension')}
                    onClick={() => void addSubDimension(section.id)}
                  >
                    <PlusIcon className={AE_ICON_SIZE} aria-hidden />
                  </BuilderIconButton>
                )}
                {!readOnly && (
                  <BuilderIconButton
                    label={t('assessmentEngine.builder.remove')}
                    variant="danger"
                    onClick={() => void removeDimension(section.id)}
                  >
                    <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                  </BuilderIconButton>
                )}
              </div>
              {participates && dim && (dim.sub_dimensions ?? []).length > 0 && (
                <ul className="mt-2 ml-3 border-l border-[var(--color-border)] space-y-2 list-none m-0 p-0">
                  {(dim.sub_dimensions ?? []).map((sub, subIdx) => (
                    <li key={sub.id} className="relative pl-4">
                      <span
                        className="pointer-events-none absolute left-0 top-1/2 w-4 border-t border-[var(--color-border)]"
                        aria-hidden
                      />
                      <div className="flex gap-2 items-center">
                        <input
                          className={`${AE_EDITABLE_INPUT} text-sm flex-1 min-w-0`}
                          value={sub.name}
                          disabled={readOnly}
                          placeholder={t('assessmentEngine.builder.subDimensionName')}
                          onChange={(e) => updateSubDimensionAt(dimIdx, subIdx, e.target.value)}
                        />
                        {!readOnly && (
                          <BuilderIconButton
                            label={t('assessmentEngine.builder.remove')}
                            variant="danger"
                            onClick={() => void removeSubDimension(sub.section_id)}
                          >
                            <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                          </BuilderIconButton>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            );
          })}
        </ul>
        )}
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={AE_BTN_SECONDARY} onClick={() => void addSection()}>
              <PlusIcon className={`${AE_ICON_SIZE} inline mr-1`} aria-hidden />
              {t('assessmentEngine.builder.addDimension')}
            </button>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
          {t('assessmentEngine.builder.scalePresets')}
        </h3>
        <p className={AE_MUTED}>{t('assessmentEngine.builder.scalePresetsHint')}</p>
        <ScalePresetsEditor
          presets={config.scale_presets ?? DEFAULT_SCALE_PRESETS}
          readOnly={readOnly}
          onChange={(scale_presets) => setConfig((c) => ({ ...c, scale_presets }))}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
          {t('assessmentEngine.builder.maturityMap')}
        </h3>
        <MaturityMapEditor
          value={config.maturity_map ?? parseMaturityMap(undefined)}
          readOnly={readOnly}
          onChange={(maturity_map) => setConfig((c) => ({ ...c, maturity_map }))}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
          {t('assessmentEngine.builder.demographicsDefaults')}
        </h3>
        <p className={AE_MUTED}>{t('assessmentEngine.builder.demographicsHint')}</p>
        <div className="max-w-xs">
          <label className={AE_LABEL}>{t('assessmentEngine.builder.demographicsPlacement')}</label>
          <select
            className={AE_SELECT}
            disabled={readOnly}
            value={config.demographics?.placement ?? 'beginning'}
            onChange={(e) =>
              setConfig((c) => ({
                ...c,
                demographics: {
                  ...c.demographics!,
                  placement: e.target.value as 'beginning' | 'end',
                },
              }))
            }
          >
            <option value="beginning">{t('assessmentEngine.builder.placementBeginning')}</option>
            <option value="end">{t('assessmentEngine.builder.placementEnd')}</option>
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {AE_DEMOGRAPHIC_FIELD_KEYS.map((key) => {
            const field = config.demographics!.fields[key];
            const labelKey =
              key === 'company_name'
                ? 'assessmentEngine.demographics.companyName'
                : `assessmentEngine.demographics.${key}`;
            return (
              <div
                key={key}
                className="flex flex-wrap items-center gap-3 border border-[var(--color-border)] rounded-md px-3 py-2"
              >
                <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={field.enabled}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        demographics: {
                          ...c.demographics!,
                          fields: {
                            ...c.demographics!.fields,
                            [key]: { ...field, enabled: e.target.checked },
                          },
                        },
                      }))
                    }
                    className="accent-[var(--color-accent-bg)]"
                  />
                  {t(labelKey)}
                </label>
                {field.enabled && (
                  <label className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)]">
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={field.required}
                      onChange={(e) =>
                        setConfig((c) => ({
                          ...c,
                          demographics: {
                            ...c.demographics!,
                            fields: {
                              ...c.demographics!.fields,
                              [key]: { ...field, required: e.target.checked },
                            },
                          },
                        }))
                      }
                      className="accent-[var(--color-accent-bg)]"
                    />
                    {t('assessmentEngine.builder.required')}
                  </label>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <button
            type="button"
            className={AE_BTN_PRIMARY}
            disabled={syncingDemo}
            onClick={() => void applyDemographics()}
          >
            {syncingDemo
              ? t('assessmentEngine.builder.saving')
              : t('assessmentEngine.builder.applyDemographics')}
          </button>
        )}
      </section>
    </div>
  );
};
